/**
 * Product Media Carousel - Clean Slyde implementation
 * Handles variant-based media filtering with dynamic reload
 */

(function() {
  const { Slyde } = window.Slyde || {};

  if (!Slyde) {
    console.error('Slyde library not loaded');
    return;
  }


  class ProductCarousel extends HTMLElement {
    constructor() {
      super();
      this._slyde = null;
      this._currentIndex = 0;
      this._hiddenContainer = null;
      this._allItems = []; // Store references to ALL items
      this._visibleSlides = []; // Cached visible slides for performance
      this._slideChangeHandler = null; // Store handler reference for cleanup
    }

    connectedCallback() {
      // Create hidden container for filtered items
      this._hiddenContainer = document.createElement('div');
      this._hiddenContainer.className = 'slyde-hidden-items';
      this._hiddenContainer.style.display = 'none';
      this.appendChild(this._hiddenContainer);

      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        // Store references to ALL media items on first init
        const wrapper = this.querySelector('.slyde-wrapper');
        if (wrapper) {
          this._allItems = Array.from(wrapper.querySelectorAll('.product__media-item'));
        }
        this._init();
      });
    }

    disconnectedCallback() {
      this._destroy();

      // Clean up hidden container
      if (this._hiddenContainer && this._hiddenContainer.parentNode) {
        this._hiddenContainer.parentNode.removeChild(this._hiddenContainer);
      }
      this._hiddenContainer = null;

      // Clear stored DOM references to prevent memory leaks
      this._allItems = [];
      this._visibleSlides = [];
    }

    _init() {
      const wrapper = this.querySelector('.slyde-wrapper');
      if (!wrapper) return;

      // Move filtered items to hidden container
      const filteredElements = Array.from(wrapper.querySelectorAll('.product__media-item.is-filtered'));
      filteredElements.forEach(el => {
        this._hiddenContainer.appendChild(el);
      });

      // Get remaining visible slides and cache them
      this._visibleSlides = Array.from(wrapper.querySelectorAll('.product__media-item'));
      const slides = this._visibleSlides;

      // Handle scrollbar visibility
      const scrollbar = this.querySelector('.slyde-scrollbar');
      if (scrollbar) {
        scrollbar.style.display = slides.length > 1 ? '' : 'none';
      }

      this.toggleAttribute('data-single', slides.length <= 1);

      // Don't initialize Slyde if 1 or fewer slides
      if (slides.length <= 1) {
        slides[0]?.classList.add('is-selected');
        return;
      }

      // Force eager loading on all images
      slides.forEach(slide => {
        slide.querySelectorAll('img[loading="lazy"]').forEach(img => {
          img.loading = 'eager';
        });
      });

      // Find initial index among visible slides
      const initialSlide = wrapper.querySelector('.is-initial-selected, .is-selected');
      let initialIndex = 0;
      if (initialSlide) {
        const slideArr = Array.from(slides);
        initialIndex = slideArr.indexOf(initialSlide);
        if (initialIndex === -1) initialIndex = 0;
      }

      // Initialize Slyde
      this._slyde = new Slyde(this, {
        loop: false,
        video: false,
        slidesPerView:1,
        freeMode: true,
        spaceBetween: 10,
        padding: {
          start: 16,
          end: 56
        },
        breakpoints: {
          741: { slidesPerView: 2 }
        }
      });

      // Set initial .is-selected class for PhotoSwipe zoom compatibility
      slides.forEach((slide, index) => {
        slide.classList.toggle('is-selected', index === initialIndex);
      });

      // Create and store slide change handler for cleanup
      this._slideChangeHandler = () => {
        const prevIndex = this._currentIndex;
        const newIndex = this._slyde.currentIndex;

        // Only update if index actually changed
        if (prevIndex === newIndex) return;

        // Update .is-selected class - only touch the 2 affected slides
        if (this._visibleSlides[prevIndex]) {
          this._visibleSlides[prevIndex].classList.remove('is-selected');
        }
        if (this._visibleSlides[newIndex]) {
          this._visibleSlides[newIndex].classList.add('is-selected');
        }

        this._currentIndex = newIndex;

        this.dispatchEvent(new CustomEvent('slide:changed', {
          detail: { index: this._currentIndex },
          bubbles: true
        }));
      };

      // Listen for slide changes
      this._slyde.on('slideChange', this._slideChangeHandler);
    }

    _destroy() {
      if (this._slyde) {
        // Remove event listener before destroying
        if (this._slideChangeHandler) {
          try {
            this._slyde.off('slideChange', this._slideChangeHandler);
          } catch (e) {}
        }
        try {
          this._slyde.destroy();
        } catch (e) {}
        this._slyde = null;
      }
      this._slideChangeHandler = null;
    }

    // Public API
    next() {
      this._slyde?.next();
    }

    prev() {
      this._slyde?.prev();
    }

    goTo(index) {
      if (this._slyde) {
        this._slyde.goTo(index);
        this._currentIndex = index;
      }
    }

    goToMediaId(mediaId) {
      const wrapper = this.querySelector('.slyde-wrapper');
      if (!wrapper) return;

      const slides = Array.from(wrapper.querySelectorAll('.product__media-item'));
      const index = slides.findIndex(s => s.dataset.mediaId === String(mediaId));
      if (index >= 0 && this._slyde) {
        this._slyde.goTo(index);
        this._currentIndex = index;
      }
    }

    get currentIndex() {
      return this._currentIndex;
    }

    get currentSlide() {
      const wrapper = this.querySelector('.slyde-wrapper');
      if (!wrapper) return null;
      const slides = wrapper.querySelectorAll('.product__media-item');
      return slides[this._currentIndex] || null;
    }

    /**
     * Get all media items (both visible and hidden) - uses stored references
     */
    getAllMediaItems() {
      return this._allItems;
    }

    /**
     * Prepares carousel for re-initialization.
     * Clears wrapper and moves all original items back.
     */
    prepareReload() {
      this._destroy();
      this._visibleSlides = [];

      const wrapper = this.querySelector('.slyde-wrapper');
      if (!wrapper) return;

      // Clear wrapper and hidden container completely
      wrapper.removeAttribute('style');
      wrapper.innerHTML = '';
      if (this._hiddenContainer) {
        this._hiddenContainer.innerHTML = '';
      }

      // Restore all original items to wrapper
      this._allItems.forEach(item => {
        wrapper.appendChild(item);
      });

      this._currentIndex = 0;
    }

    /**
     * Public init method - call after prepareReload() and updating is-filtered classes
     */
    init() {
      this._init();
    }

    /**
     * Full reload - prepares and re-initializes in one call.
     */
    reload() {
      this.prepareReload();
      this._init();
    }
  }

  if (!customElements.get('product-carousel')) {
    customElements.define('product-carousel', ProductCarousel);
  }
})();
